import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Compass, PenTool, Calculator, HardHat, KeyRound } from "lucide-react";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import SEO from "@/components/SEO";

const steps = [
  { icon: Compass, title: "Sopralluogo", desc: "Il nostro tecnico visita l'immobile, ascolta le esigenze e prende le misure." },
  { icon: PenTool, title: "Progetto architettonico", desc: "Layout, render 3D, scelta materiali con i nostri architetti interni." },
  { icon: Calculator, title: "Preventivo dettagliato", desc: "Voce per voce, senza sorprese. Validato dal nostro ufficio tecnico." },
  { icon: HardHat, title: "Cantiere", desc: "Squadre interne, direzione lavori quotidiana, controllo tempi e qualità." },
  { icon: KeyRound, title: "Consegna chiavi in mano", desc: "Collaudo finale, documentazione completa e garanzia sui lavori." },
];

const HERO =
  "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?auto=format&fit=crop&w=2400&q=85";

export default function Ristrutturazioni() {
  return (
    <div>
      <SEO
        title="Ristrutturazione appartamenti Napoli — chiavi in mano | Ri.Sco."
        description="Ristrutturazione appartamenti a Napoli e provincia con team interno di architetti e geometri. Progetto, cantiere e consegna chiavi in mano. Preventivo gratuito con simulatore AI."
        path="/ristrutturazioni"
      />
      <section className="relative min-h-[80vh] flex items-end overflow-hidden -mt-20 pt-20" data-testid="ristrutt-hero">
        <div className="absolute inset-0">
          <img src={HERO} alt="Interni ristrutturati" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-risco-ink/95 via-risco-ink/60 to-risco-ink/30" />
        </div>
        <div className="relative container-x py-20 md:py-28 w-full">
          <div className="grid lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8">
              <div className="rule-tag text-stone-300 text-xs uppercase tracking-[0.28em] mb-8">Servizio · 01</div>
              <h1 className="text-risco-bg leading-[0.95]">
                <span className="font-display italic text-5xl sm:text-6xl md:text-7xl lg:text-8xl block">Ristrutturazioni</span>
                <span className="font-heading font-medium text-3xl sm:text-4xl md:text-5xl lg:text-7xl block tracking-tighter mt-2">
                  chiavi in mano, interni e fabbricati.
                </span>
              </h1>
            </div>
            <div className="lg:col-span-4 lg:pl-8">
              <p className="text-stone-200 text-base leading-relaxed max-w-md">
                Ristrutturiamo appartamenti, uffici, negozi e interi fabbricati. Progettazione architettonica interna,
                direzione lavori, squadre specializzate.
              </p>
              <Link
                to="/simulatore"
                data-testid="ristrutt-hero-cta"
                className="mt-8 inline-flex items-center justify-between gap-3 px-6 py-4 rounded-full bg-risco-accent hover:bg-risco-accent-dark text-white font-medium transition-colors"
              >
                Analizza il tuo progetto con l'AI <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24" data-testid="ristrutt-process">
        <div className="container-x">
          <div className="overline mb-3">Processo</div>
          <h2 className="font-heading text-3xl sm:text-4xl text-risco-ink tracking-tight">Cinque tappe. Zero sorprese.</h2>
          <div className="grid md:grid-cols-5 gap-4 mt-10">
            {steps.map((s, i) => (
              <div key={s.title} className="risco-card p-6 relative" data-testid={`step-${i + 1}`}>
                <div className="absolute -top-3 -left-3 w-8 h-8 rounded-md bg-risco-accent text-white flex items-center justify-center font-heading font-bold text-sm">{i + 1}</div>
                <s.icon className="w-6 h-6 text-risco-accent mb-3 mt-2" />
                <div className="font-heading text-lg text-risco-ink">{s.title}</div>
                <div className="text-sm text-risco-muted mt-2">{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-risco-alt border-y border-risco-border" data-testid="ristrutt-design">
        <div className="container-x grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="overline mb-3">Progettazione interna</div>
            <h2 className="font-heading text-3xl sm:text-4xl text-risco-ink tracking-tight">Architetti nostri, render inclusi</h2>
            <p className="text-risco-muted mt-5">
              La progettazione degli interni è il cuore di ogni ristrutturazione. Non è un servizio esterno:
              i nostri architetti lavorano fianco a fianco con l'impresa. Ti mostriamo il progetto prima di iniziare,
              scegliamo insieme materiali e finiture, gestiamo la direzione lavori.
            </p>
            <ul className="mt-6 space-y-3 text-stone-700">
              {["Layout distributivo e render 3D", "Scelta materiali con moodboard", "Pratiche edilizie SCIA / CILA", "Direzione lavori quotidiana"].map((it) => (
                <li key={it} className="flex items-start gap-2"><span className="text-risco-accent mt-1">◆</span>{it}</li>
              ))}
            </ul>
          </div>
          <BeforeAfterSlider
            before="https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=1600&q=80"
            after="https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?auto=format&fit=crop&w=1600&q=80"
            height={460}
          />
        </div>
      </section>

      <section className="py-20 md:py-24" data-testid="ristrutt-cta">
        <div className="container-x risco-card p-10 md:p-14 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl text-risco-ink tracking-tight">Pronto a partire?</h2>
          <p className="text-risco-muted mt-4 max-w-2xl mx-auto">Descrivi il tuo intervento: la nostra AI produce un'analisi con fasi, permessi tipici, voci di costo e tempi. Poi fissiamo un sopralluogo gratuito.</p>
          <Link
            to="/simulatore"
            data-testid="ristrutt-bottom-cta"
            className="mt-8 inline-flex items-center gap-2 px-6 py-4 rounded-md bg-risco-accent hover:bg-risco-accent-dark text-white font-semibold"
          >
            Preventivo Smart AI <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
